import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCog,
    faFileImport,
    faRefresh,
    faThumbsUp,
} from '@fortawesome/free-solid-svg-icons';
import { LinearProgress } from '@mui/material';
import { withRouter } from 'react-router-dom';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import {
    PageHeading,
    PageWrap,
    ToastMessage,
} from '../common/forms/FormElements';
import { util } from '../Util';
import { Button } from 'reactstrap';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import { ImportTrackersDisplay } from './ImportTrackersDisplay';
import { HeavyBidUploadButton } from './HeavyBidUploadButton';
import './ImportIndex.scss';
import { ImportSystems } from './Import';

class ImportIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            saving: false,
            importSystems: [],
            importTrackers: [],
            importTrackerStatuses: [],
        };

        this.uploadRef = React.createRef();
    }

    componentDidMount() {
        return this.populateState();
    }

    async populateState() {
        this.setState({ loading: true });

        const [importSystems, importTrackerStatuses] = await Promise.all([
            util.fetch.js(ApiRoutes.importTracker.systems()),
            util.fetch.js(ApiRoutes.importTracker.statuses()),
        ]);

        this.setState(
            {
                importTrackerStatuses,
                importSystems,
            },
            () => this.refreshTrackers()
        );
    }

    refreshTrackers = async (showToast) => {
        const importTrackers = await util.fetch.js(
            ApiRoutes.importTracker.all()
        );

        this.setState({ importTrackers, loading: false });

        if (showToast) {
            toast.success(
                <ToastMessage
                    icon={faThumbsUp}
                    header="Import Results Refreshed"
                    message="Import results have been reloaded."
                />
            );
        }
    };

    startImport = () => {
        this.setState({ saving: true });

        // imports may take a while; this is intentionally fire-and-forget
        util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.vistaImport.start(),
            {},
            'Error Importing',
            () => {
                this.setState({ saving: false });
                this.refreshTrackers(true);
            },
            false
        );

        setTimeout(() => this.refreshTrackers(), 250);
    };

    startArcGISOnlineImport = () => {
        this.setState({ saving: true });

        // imports may take a while; this is intentionally fire-and-forget
        util.fetch
            .base(
                ApiRoutes.import.arcGisOnline(),
                util.fetch.types.post,
                null,
                util.fetch.format.none,
                util.fetch.timeout.none
            )
            .catch((e) => {
                toast.error(
                    <ToastMessage
                        header="ArcGIS Online Import"
                        message="We were unable to import ArcGIS Online data."
                    />
                );
            })
            .finally(() => {
                this.setState({ saving: false });
                this.refreshTrackers(true);
            });

        setTimeout(() => this.refreshTrackers(), 250);
    };

    isFunctionallySaving = () => {
        const { saving, importTrackers, importTrackerStatuses } = this.state;

        if (saving) {
            return true;
        }

        if (!importTrackers || !importTrackers.length) {
            return false;
        }

        const firstImportTracker = importTrackers[0];

        if (firstImportTracker) {
            const startTime = moment(firstImportTracker.startTime);
            const now = moment(new Date());

            const minutes = now.diff(startTime, 'minutes', true);

            // if the last import has been running for more than 15 minutes, let them
            // re-start
            if (minutes > 15) {
                return false;
            }
        }

        const importTrackerStatus = importTrackerStatuses.find(
            (its) => its.id == firstImportTracker.importTrackerStatusId
        );

        return importTrackerStatus && importTrackerStatus.inProcess;
    };

    getRunningImportTracker = () => {
        if (!this.isFunctionallySaving()) {
            return {};
        }

        const { importTrackers } = this.state;

        if (!importTrackers || importTrackers.length == 0) {
            return {};
        }

        return importTrackers[0];
    };

    render() {
        const { loading } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        const functionallySaving = this.isFunctionallySaving();

        const runningImportTracker = this.getRunningImportTracker();

        return (
            <PageWrap className="auto-overflow">
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faFileImport}
                        className="mr-2 text-muted"
                    />
                    <span>System Imports</span>
                </PageHeading>

                <div className="d-flex mb-3">
                    <div className="w-25">
                        <div>
                            <Button
                                size="xl"
                                className="w-50 mt-2"
                                outline={functionallySaving}
                                color="info"
                                disabled={functionallySaving}
                                onClick={this.startImport}
                            >
                                <FontAwesomeIcon
                                    icon={faCog}
                                    className="mr-2"
                                    spin={
                                        functionallySaving &&
                                        runningImportTracker.importSystemId ==
                                            ImportSystems.Vista
                                    }
                                />
                                Run Vista Import
                            </Button>
                        </div>
                        <div>
                            <Button
                                size="xl"
                                className="w-50 mt-3"
                                outline={functionallySaving}
                                color="warning"
                                disabled={functionallySaving}
                                onClick={this.startArcGISOnlineImport}
                            >
                                <FontAwesomeIcon
                                    icon={faCog}
                                    className="mr-2"
                                    spin={
                                        functionallySaving &&
                                        runningImportTracker.importSystemId ==
                                            ImportSystems.ArcGisOnline
                                    }
                                />
                                Run ArcGIS Online Import
                            </Button>
                        </div>
                    </div>

                    <div className="w-50">
                        <HeavyBidUploadButton
                            onHeavyBidUploadComplete={this.refreshTrackers}
                        />
                    </div>

                    <div className="w-25 text-right">
                        <Button
                            size="xl mt-5"
                            color="success"
                            onClick={this.refreshTrackers}
                        >
                            <FontAwesomeIcon
                                icon={faRefresh}
                                className="mr-2"
                            />
                            Refresh Import Details
                        </Button>
                    </div>
                </div>

                <ImportTrackersDisplay {...this.state} />
            </PageWrap>
        );
    }
}
export default withRouter(ImportIndex);
